import axios from 'axios';
import moment from 'moment';
import { API_BASE_URL } from '@config';

// * ////////////// ACTION TYPES ////////////// * //

export const SET_STUDENT_ACTIVITY_TRAININGS = 'SET_STUDENT_ACTIVITY_TRAININGS';
export const SET_STUDENT_ACTIVITY_SELECTED_TRAINING = 'SET_STUDENT_ACTIVITY_SELECTED_TRAINING';
export const SET_STUDENT_ACTIVITY_RANGE = 'SET_STUDENT_ACTIVITY_RANGE';

export const SET_STUDENT_ACTIVITY_SUMMARY = 'SET_STUDENT_ACTIVITY_SUMMARY';
export const SET_STUDENT_ACTIVITY_SUMMARY_LOADING = 'SET_STUDENT_ACTIVITY_SUMMARY_LOADING';

export const SET_STUDENT_ACTIVITY_DETAIL = 'SET_STUDENT_ACTIVITY_DETAIL';
export const SET_STUDENT_ACTIVITY_DETAIL_LOADING = 'SET_STUDENT_ACTIVITY_DETAIL_LOADING';

export const SET_STUDENT_ACTIVITY_SNAPSHOTS = 'SET_STUDENT_ACTIVITY_SNAPSHOTS';
export const SET_STUDENT_ACTIVITY_SNAPSHOTS_LOADING = 'SET_STUDENT_ACTIVITY_SNAPSHOTS_LOADING';

export const SET_STUDENT_ACTIVITY_ERROR = 'SET_STUDENT_ACTIVITY_ERROR';
export const CLEAR_STUDENT_ACTIVITY_DETAIL = 'CLEAR_STUDENT_ACTIVITY_DETAIL';

// * ////////////// RANGE HELPER ////////////// * //

// The date-range presets are expressed as a day count. Given a count we
// derive an inclusive [from, to] window ending today, formatted as the
// YYYY-MM-DD strings the API expects. Kept here so the sync action and any
// caller that needs to seed a default window agree on the exact math.
export const rangeFromDays = days => {
	const to = moment().format('YYYY-MM-DD');
	const from = moment().subtract(days - 1, 'days').format('YYYY-MM-DD');
	return { days, from, to };
};

// * ////////////// SYNC ACTIONS ////////////// * //

export const setStudentActivityTrainings = trainings => ({
	type: SET_STUDENT_ACTIVITY_TRAININGS,
	payload: trainings
});

export const setSelectedTraining = trainingId => ({
	type: SET_STUDENT_ACTIVITY_SELECTED_TRAINING,
	payload: trainingId
});

export const setRange = days => ({
	type: SET_STUDENT_ACTIVITY_RANGE,
	payload: rangeFromDays(days)
});

export const setStudentActivityError = error => ({
	type: SET_STUDENT_ACTIVITY_ERROR,
	payload: error
});

export const clearStudentActivityDetail = () => ({
	type: CLEAR_STUDENT_ACTIVITY_DETAIL
});

// * ////////////// THUNKS ////////////// * //

// Reuses the same endpoint as the Workshops page. The API already scopes
// this to the instructor's own trainings, so no extra filtering is needed.
export const getTrainings = () => async dispatch => {
	try {
		const response = await axios.get(`${API_BASE_URL}/api/trainings/`);
		dispatch(setStudentActivityTrainings(response.data || []));
		return response.data;
	} catch (error) {
		dispatch(setStudentActivityError(error.response?.data?.message || 'Failed to load workshops'));
		throw error;
	}
};

export const getTrainingActivity = (trainingId, from, to) => async dispatch => {
	dispatch({ type: SET_STUDENT_ACTIVITY_SUMMARY_LOADING, payload: true });
	try {
		const response = await axios.get(
			`${API_BASE_URL}/api/trainings/${trainingId}/student-activity`,
			{ params: { from, to } }
		);
		dispatch({ type: SET_STUDENT_ACTIVITY_SUMMARY, payload: response.data });
		return response.data;
	} catch (error) {
		dispatch(setStudentActivityError(error.response?.data?.message || 'Failed to load student activity'));
		dispatch({ type: SET_STUDENT_ACTIVITY_SUMMARY_LOADING, payload: false });
		throw error;
	}
};

// Persist the workshop's Focus Index weighting ('auto' = completion-anchored
// when courses exist, 'activity' = presence signals only), then reload the
// summary so scores reflect the new mode immediately.
export const updateScoreMode = (trainingId, scoreMode, from, to) => async dispatch => {
	try {
		await axios.patch(
			`${API_BASE_URL}/api/trainings/${trainingId}/student-activity/settings`,
			{ scoreMode }
		);
		return dispatch(getTrainingActivity(trainingId, from, to));
	} catch (error) {
		dispatch(setStudentActivityError(error.response?.data?.message || 'Failed to update scoring mode'));
		throw error;
	}
};

export const getStudentDetail = (trainingId, username, from, to) => async dispatch => {
	dispatch({ type: SET_STUDENT_ACTIVITY_DETAIL_LOADING, payload: true });
	try {
		const response = await axios.get(
			`${API_BASE_URL}/api/trainings/${trainingId}/student-activity/${encodeURIComponent(username)}`,
			{ params: { from, to } }
		);
		dispatch({ type: SET_STUDENT_ACTIVITY_DETAIL, payload: response.data });
		return response.data;
	} catch (error) {
		dispatch(setStudentActivityError(error.response?.data?.message || 'Failed to load student detail'));
		dispatch({ type: SET_STUDENT_ACTIVITY_DETAIL_LOADING, payload: false });
		throw error;
	}
};

export const getSnapshots = (trainingId, username, from, to) => async dispatch => {
	dispatch({ type: SET_STUDENT_ACTIVITY_SNAPSHOTS_LOADING, payload: true });
	try {
		const response = await axios.get(
			`${API_BASE_URL}/api/trainings/${trainingId}/student-activity/${encodeURIComponent(username)}/snapshots`,
			{ params: { from, to } }
		);
		dispatch({
			type: SET_STUDENT_ACTIVITY_SNAPSHOTS,
			payload: { snapshots: response.data?.snapshots || [], policy: response.data?.policy || null }
		});
		return response.data;
	} catch (error) {
		dispatch(setStudentActivityError(error.response?.data?.message || 'Failed to load screenshots'));
		dispatch({ type: SET_STUDENT_ACTIVITY_SNAPSHOTS_LOADING, payload: false });
		throw error;
	}
};

// Snapshot JPEGs are auth-gated behind the Bearer token, which cannot be
// attached to a plain <img src>. Mirror MultiVncView's getDesktopSnapshotObjectUrl:
// fetch the bytes as a blob (the global axios Authorization header rides along)
// and hand back an object URL. The caller owns the URL and must revoke it via
// URL.revokeObjectURL on unmount to avoid leaking Blobs. `url` is the relative
// API path returned by the snapshots endpoint.
export const getSnapshotObjectUrl = url => async () => {
	try {
		const response = await axios.get(`${API_BASE_URL}${url}`, { responseType: 'blob' });
		return { success: true, objectUrl: URL.createObjectURL(response.data) };
	} catch (error) {
		return { success: false, error: error.message || 'snapshot_fetch_failed' };
	}
};
